import { Alert, SxProps, Theme, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useEffect, useState } from "react";
import { isTodayInTimezone } from "./isTodayInTimezone";

dayjs.extend(utc);

const MELT_PLAN_STALE_AFTER_MS = 2 * 60 * 60 * 1000;
const CLOCK_TICK_MS = 60_000;

const TIME_OF_DAY_FORMAT = "h:mm A";

const useNow = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, CLOCK_TICK_MS);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return now;
};

const useFormatPlanUpdatedAt = () => {
  const { toLocalDatetime, localTimezone } = useLocalDatetime();
  return (planUpdatedAt: string): string => {
    const isToday = isTodayInTimezone({
      timestamp: planUpdatedAt,
      timezone: localTimezone,
    });
    return toLocalDatetime(
      planUpdatedAt,
      isToday ? TIME_OF_DAY_FORMAT : `MMM D, ${TIME_OF_DAY_FORMAT}`
    );
  };
};

export const MeltPlanUpdatedAt = ({
  planUpdatedAt,
}: {
  planUpdatedAt: string | null;
}) => {
  const { t } = useTenantTranslation();
  const formatPlanUpdatedAt = useFormatPlanUpdatedAt();
  if (planUpdatedAt === null) {
    return null;
  }
  return (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {t("meltPlanLastUpdated", { time: formatPlanUpdatedAt(planUpdatedAt) })}
    </Typography>
  );
};

export const MeltPlanStaleAlert = ({
  planUpdatedAt,
  sx,
}: {
  planUpdatedAt: string | null;
  sx?: SxProps<Theme>;
}) => {
  const { t } = useTenantTranslation();
  const formatPlanUpdatedAt = useFormatPlanUpdatedAt();
  const now = useNow();
  if (
    planUpdatedAt === null ||
    now - dayjs.utc(planUpdatedAt).valueOf() <= MELT_PLAN_STALE_AFTER_MS
  ) {
    return null;
  }
  return (
    <Alert severity="warning" sx={sx}>
      {t("meltPlanStale", { time: formatPlanUpdatedAt(planUpdatedAt) })}
    </Alert>
  );
};
